<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  fetchMarketplaceJson,
  postMarketplaceJson,
  postMarketplaceJsonBody,
} from 'dashboard/helpers/marketplaceApi';
import { formatCurrency } from '../helpers/orderHelpers';
import { useAdsSpeech } from '../composables/useAdsSpeech';

const route = useRoute();

// --- Tarih yardimcilari (Turkiye saatine gore) ---
const TR_OFFSET_MS = 3 * 60 * 60 * 1000;
const toStr = d => d.toISOString().slice(0, 10);
const todayTR = () => toStr(new Date(Date.now() + TR_OFFSET_MS));
const addDays = (s, n) => {
  const d = new Date(`${s}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return toStr(d);
};
const firstOfMonth = s => `${s.slice(0, 7)}-01`;
const firstOfYear = s => `${s.slice(0, 4)}-01-01`;
const mondayOf = s => {
  const day = new Date(`${s}T00:00:00Z`).getUTCDay();
  return addDays(s, day === 0 ? -6 : -(day - 1));
};

const ACCOUNT_TABS = [
  { key: 'all', label: 'Tüm Hesaplar' },
  { key: 'koza', label: 'Koza Butik' },
  { key: 'modax', label: 'ModaXlarge.com' },
  { key: 'modax_shopify', label: 'ModaXLarge (Shopify)' },
];
const GRANULARITY_TABS = [
  { key: 'day', label: 'Günlük' },
  { key: 'month', label: 'Aylık' },
  { key: 'year', label: 'Yıllık' },
];

const today = todayTR();
const startDate = ref(firstOfMonth(today));
const endDate = ref(today);
const granularity = ref('day');
const accountKey = ref('all');

const isLoading = ref(false);
const loadError = ref('');
const report = ref(null);

// --- AI yorumu ---
const isAnalyzing = ref(false);
const analysis = ref(null);
const analysisError = ref('');

const qs = () => {
  const params = new URLSearchParams({
    start: startDate.value,
    end: endDate.value,
    granularity: granularity.value,
  });
  if (accountKey.value !== 'all') params.set('hesap', accountKey.value);
  return params.toString();
};

const loadReport = async () => {
  isLoading.value = true;
  loadError.value = '';
  analysis.value = null;
  stopSpeaking();
  try {
    report.value = await fetchMarketplaceJson(
      route.params.accountId,
      `meta_ads/report?${qs()}`
    );
  } catch {
    loadError.value = 'Rapor verisi yüklenemedi.';
  } finally {
    isLoading.value = false;
  }
};

// --- Günlük not (Semra Hanım talebi) ---
// Rakamlar Meta'dan gelir; bu alan "o gün ne yaptık / ne oldu" bilgisini
// tutar. Boş bırakılırsa kayıt silinir. Kaydetme satır bazlıdır, sayfa
// yeniden yüklenmez — not yazarken rapor kaybolmasın.
const noteDrafts = ref({});
const noteSaving = ref({});
const noteSaved = ref({});

const noteValue = row =>
  noteDrafts.value[row.period] !== undefined
    ? noteDrafts.value[row.period]
    : row.note || '';

const saveNote = async row => {
  const value = noteValue(row);
  noteSaving.value = { ...noteSaving.value, [row.period]: true };
  try {
    await postMarketplaceJsonBody(
      route.params.accountId,
      `meta_ads/report_note?${qs()}`,
      { date: row.period, note: value }
    );
    row.note = value;
    noteSaved.value = { ...noteSaved.value, [row.period]: true };
    setTimeout(() => {
      noteSaved.value = { ...noteSaved.value, [row.period]: false };
    }, 2000);
  } catch {
    loadError.value = 'Not kaydedilemedi.';
  } finally {
    noteSaving.value = { ...noteSaving.value, [row.period]: false };
  }
};

const roasClass = value => {
  if (value === null || value === undefined) return 'text-n-slate-10';
  if (value >= 6) return 'text-n-teal-11 font-bold';
  if (value >= 3) return 'text-n-slate-12 font-semibold';
  return 'text-n-ruby-11 font-bold';
};

const runAnalysis = async () => {
  isAnalyzing.value = true;
  analysisError.value = '';
  try {
    analysis.value = await postMarketplaceJson(
      route.params.accountId,
      `meta_ads/report_analyze?${qs()}`
    );
  } catch {
    analysisError.value = 'Analiz alınamadı, tekrar deneyin.';
  } finally {
    isAnalyzing.value = false;
  }
};

const applyPreset = preset => {
  if (preset === 'today') {
    startDate.value = today;
    endDate.value = today;
    granularity.value = 'day';
  } else if (preset === 'yesterday') {
    const y = addDays(today, -1);
    startDate.value = y;
    endDate.value = y;
    granularity.value = 'day';
  } else if (preset === 'week') {
    startDate.value = mondayOf(today);
    endDate.value = today;
    granularity.value = 'day';
  } else if (preset === 'month') {
    startDate.value = firstOfMonth(today);
    endDate.value = today;
    granularity.value = 'day';
  } else if (preset === 'last30') {
    startDate.value = addDays(today, -29);
    endDate.value = today;
    granularity.value = 'day';
  } else if (preset === 'year') {
    startDate.value = firstOfYear(today);
    endDate.value = today;
    granularity.value = 'month';
  } else if (preset === 'all') {
    startDate.value =
      report.value?.meta?.earliestAvailable || addDays(today, -365);
    endDate.value = today;
    granularity.value = 'month';
  }
  loadReport();
};

onMounted(loadReport);

// --- Sesli okuma (paylaşılan motor) — düşük görme kullanımı için ---
const { isSpeaking, isLoadingAudio, toggleSpeaking, stopSpeaking } =
  useAdsSpeech();

const buildSpeechText = () => {
  if (!analysis.value) return '';
  const a = analysis.value;
  const parts = [a.headline, a.narrative];
  if (a.highlights?.length) {
    parts.push('Öne çıkanlar.');
    parts.push(a.highlights.join('. '));
  }
  if (a.actions?.length) {
    parts.push('Önerilen adımlar.');
    a.actions.forEach(ac => parts.push(`${ac.title}. ${ac.detail}`));
  }
  return parts.filter(Boolean).join('. ');
};

// --- Görsel yardimcilar ---
const totals = computed(() => report.value?.totals || {});
const num = n => Number(n || 0).toLocaleString('tr-TR');
const maxSeriesSpend = computed(() =>
  Math.max(1, ...(report.value?.series || []).map(s => Number(s.spend || 0)))
);
const rangeText = computed(() => {
  if (!report.value) return '';
  const fmt = s => {
    const [y, m, d] = s.split('-');
    return `${d}.${m}.${y}`;
  };
  return `${fmt(startDate.value)} – ${fmt(endDate.value)}`;
});
const FUNNEL_STAGES = [
  { key: 'impressions', label: 'Gösterim' },
  { key: 'linkClicks', label: 'Link Tıklama' },
  { key: 'landingViews', label: 'Siteye Geliş' },
  { key: 'viewContent', label: 'Ürün Görüntüleme' },
  { key: 'addToCart', label: 'Sepete Ekleme' },
  { key: 'checkout', label: 'Ödemeye Başlama' },
  { key: 'purchase', label: 'Satın Alma' },
];
const funnelRows = computed(() => {
  const f = report.value?.funnel || {};
  const top = Math.max(1, f.impressions || 0);
  return FUNNEL_STAGES.map(s => ({
    label: s.label,
    value: f[s.key] || 0,
    pct: Math.round(((f[s.key] || 0) / top) * 100),
  }));
});
const accountLabel = key => report.value?.meta?.accountLabels?.[key] || key;
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between gap-4 px-5 py-4 border-b sm:px-7 border-n-weak flex-shrink-0"
    >
      <div class="min-w-0">
        <h1 class="text-2xl font-bold text-n-slate-12">Reklam Raporları</h1>
        <p class="text-base text-n-slate-11">
          Seçtiğin tarih aralığında harcama, dönüşüm ve yapay zekâ yorumu
        </p>
      </div>
      <button
        type="button"
        class="flex items-center gap-2 px-4 py-2.5 text-base font-semibold rounded-lg text-n-slate-12 outline outline-1 outline-n-weak hover:bg-n-alpha-1 flex-shrink-0"
        :disabled="isLoading"
        @click="loadReport"
      >
        <span
          class="size-5 i-lucide-refresh-cw"
          :class="{ 'animate-spin': isLoading }"
        />
        Yenile
      </button>
    </header>

    <div class="flex-1 overflow-y-auto">
      <div class="px-5 py-6 mx-auto max-w-6xl sm:px-7">
        <!-- Filtreler -->
        <div class="flex flex-col gap-4 mb-6">
          <!-- Hesap -->
          <div class="flex flex-wrap gap-2">
            <button
              v-for="tab in ACCOUNT_TABS"
              :key="tab.key"
              type="button"
              class="px-4 py-2.5 text-base font-semibold rounded-lg"
              :class="
                accountKey === tab.key
                  ? 'bg-n-brand text-white'
                  : 'bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1'
              "
              @click="
                accountKey = tab.key;
                loadReport();
              "
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Hazir aralik + granularite -->
          <div class="flex flex-wrap items-center gap-2">
            <button
              v-for="p in [
                { k: 'today', l: 'Bugün' },
                { k: 'yesterday', l: 'Dün' },
                { k: 'week', l: 'Bu Hafta' },
                { k: 'month', l: 'Bu Ay' },
                { k: 'last30', l: 'Son 30 Gün' },
                { k: 'year', l: 'Bu Yıl' },
                { k: 'all', l: 'Tüm Zamanlar' },
              ]"
              :key="p.k"
              type="button"
              class="px-3.5 py-2 text-sm font-medium rounded-full bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-1"
              @click="applyPreset(p.k)"
            >
              {{ p.l }}
            </button>
          </div>

          <!-- Ozel tarih + granularite -->
          <div class="flex flex-wrap items-center gap-3">
            <input
              v-model="startDate"
              type="date"
              class="px-3 py-2 text-base rounded-lg bg-n-solid-2 text-n-slate-12 outline outline-1 outline-n-weak"
            />
            <span class="text-n-slate-11">→</span>
            <input
              v-model="endDate"
              type="date"
              class="px-3 py-2 text-base rounded-lg bg-n-solid-2 text-n-slate-12 outline outline-1 outline-n-weak"
            />
            <div class="flex gap-1 p-1 rounded-lg bg-n-solid-2">
              <button
                v-for="g in GRANULARITY_TABS"
                :key="g.key"
                type="button"
                class="px-3.5 py-1.5 text-sm font-semibold rounded-md"
                :class="
                  granularity === g.key
                    ? 'bg-n-brand text-white'
                    : 'text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="
                  granularity = g.key;
                  loadReport();
                "
              >
                {{ g.label }}
              </button>
            </div>
            <button
              type="button"
              class="px-4 py-2 text-base font-semibold text-white rounded-lg bg-n-brand"
              @click="loadReport"
            >
              Uygula
            </button>
          </div>
        </div>

        <div
          v-if="isLoading"
          class="flex flex-col items-center justify-center gap-3 py-20 text-n-slate-11"
        >
          <Spinner />
          <p class="text-base">Rapor hazırlanıyor…</p>
        </div>

        <div
          v-else-if="loadError"
          class="flex flex-col items-center gap-3 py-20 text-n-slate-11"
        >
          <span class="size-10 i-lucide-alert-triangle text-n-ruby-9" />
          <p class="text-base">{{ loadError }}</p>
        </div>

        <template v-else-if="report">
          <!-- ============ AI YORUMU (en önemli, en üstte) ============ -->
          <section
            class="p-5 mb-6 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak sm:p-6"
          >
            <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h2
                class="flex items-center gap-2 text-xl font-bold text-n-slate-12"
              >
                <span class="size-6 i-lucide-sparkles text-n-brand" />
                Yapay Zekâ Yorumu
                <span class="text-base font-normal text-n-slate-11"
                  >({{ rangeText }})</span
                >
              </h2>
              <div class="flex gap-2">
                <button
                  v-if="analysis"
                  type="button"
                  class="flex items-center gap-2 px-4 py-2.5 text-base font-semibold rounded-lg outline outline-1"
                  :class="
                    isSpeaking || isLoadingAudio
                      ? 'bg-n-ruby-9 text-white outline-n-ruby-9'
                      : 'bg-n-teal-9 text-white outline-n-teal-9'
                  "
                  @click="toggleSpeaking(buildSpeechText())"
                >
                  <Spinner v-if="isLoadingAudio" class="size-5" />
                  <span
                    v-else
                    :class="
                      isSpeaking ? 'i-lucide-square' : 'i-lucide-volume-2'
                    "
                    class="size-5"
                  />
                  {{
                    isLoadingAudio
                      ? 'Ses hazırlanıyor…'
                      : isSpeaking
                        ? 'Durdur'
                        : 'Sesli Dinle'
                  }}
                </button>
                <button
                  type="button"
                  class="flex items-center gap-2 px-4 py-2.5 text-base font-semibold text-white rounded-lg bg-n-brand disabled:opacity-60"
                  :disabled="isAnalyzing"
                  @click="runAnalysis"
                >
                  <Spinner v-if="isAnalyzing" class="size-5" />
                  <span v-else class="size-5 i-lucide-wand-2" />
                  {{ analysis ? 'Yeniden Analiz Et' : 'Analiz Et' }}
                </button>
              </div>
            </div>

            <p v-if="analysisError" class="text-base text-n-ruby-11">
              {{ analysisError }}
            </p>

            <div
              v-else-if="!analysis && !isAnalyzing"
              class="text-base text-n-slate-11"
            >
              Bu dönemin verisini yapay zekâya yorumlatmak için
              <strong>“Analiz Et”</strong>e bas. Sonucu sesli de
              dinleyebilirsin.
            </div>

            <div v-else-if="isAnalyzing" class="text-base text-n-slate-11">
              Yapay zekâ verileri inceliyor, birkaç saniye…
            </div>

            <div v-else class="flex flex-col gap-4">
              <p class="text-2xl font-bold leading-snug text-n-slate-12">
                {{ analysis.headline }}
              </p>
              <p
                class="text-lg leading-relaxed whitespace-pre-line text-n-slate-12"
              >
                {{ analysis.narrative }}
              </p>

              <div v-if="analysis.highlights?.length">
                <h3 class="mb-2 text-lg font-bold text-n-slate-12">
                  Öne Çıkanlar
                </h3>
                <ul class="flex flex-col gap-2">
                  <li
                    v-for="(h, i) in analysis.highlights"
                    :key="i"
                    class="flex items-start gap-2 text-lg text-n-slate-12"
                  >
                    <span
                      class="flex-shrink-0 mt-1.5 size-2 rounded-full bg-n-brand"
                    />
                    {{ h }}
                  </li>
                </ul>
              </div>

              <div v-if="analysis.actions?.length">
                <h3 class="mb-2 text-lg font-bold text-n-slate-12">
                  Önerilen Adımlar
                </h3>
                <div class="flex flex-col gap-2">
                  <div
                    v-for="(ac, i) in analysis.actions"
                    :key="i"
                    class="p-3 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
                  >
                    <p class="text-lg font-semibold text-n-slate-12">
                      {{ ac.title }}
                    </p>
                    <p class="text-base text-n-slate-11">{{ ac.detail }}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- ============ KPI ============ -->
          <div class="grid grid-cols-2 gap-3 mb-6 lg:grid-cols-3">
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Toplam Harcama</p>
              <p class="mt-1 text-3xl font-bold text-n-slate-12">
                {{ formatCurrency(totals.spend) }}
              </p>
            </div>
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Satın Alma (Meta)</p>
              <p class="mt-1 text-3xl font-bold text-n-slate-12">
                {{ num(totals.metaPurchases) }}
              </p>
            </div>
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Mesajlaşma</p>
              <p class="mt-1 text-3xl font-bold text-n-slate-12">
                {{ num(totals.messagingConversations) }}
              </p>
            </div>
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Tıklama</p>
              <p class="mt-1 text-3xl font-bold text-n-slate-12">
                {{ num(totals.clicks) }}
              </p>
            </div>
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Tıklama Oranı (CTR)</p>
              <p class="mt-1 text-3xl font-bold text-n-slate-12">
                %{{ num(totals.ctr) }}
              </p>
            </div>
            <div
              class="p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
            >
              <p class="text-base text-n-slate-11">Satın Alma Başı Maliyet</p>
              <p class="mt-1 text-3xl font-bold text-n-slate-12">
                {{
                  totals.costPerPurchase
                    ? formatCurrency(totals.costPerPurchase)
                    : '—'
                }}
              </p>
            </div>
          </div>

          <!-- ============ Hesap kırılımı ============ -->
          <section
            v-if="(report.perAccount || []).length"
            class="p-5 mb-6 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <h2 class="mb-3 text-xl font-bold text-n-slate-12">
              Hesaplara Göre Harcama
            </h2>
            <div class="flex flex-col gap-3">
              <div
                v-for="a in report.perAccount"
                :key="a.accountKey"
                class="flex items-center justify-between gap-4 p-3 rounded-xl bg-n-solid-1"
              >
                <span class="text-lg font-semibold text-n-slate-12">{{
                  accountLabel(a.accountKey)
                }}</span>
                <div class="flex items-center gap-5 text-right">
                  <span class="text-lg font-bold text-n-slate-12">{{
                    formatCurrency(a.spend)
                  }}</span>
                  <span class="text-base text-n-slate-11"
                    >{{ num(a.metaPurchases) }} satın alma</span
                  >
                </div>
              </div>
            </div>
          </section>

          <!-- ============ Zaman serisi ============ -->
          <section
            v-if="(report.series || []).length"
            class="p-5 mb-6 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <h2 class="mb-4 text-xl font-bold text-n-slate-12">
              Harcama Akışı
            </h2>
            <div class="flex flex-col gap-2">
              <div
                v-for="s in report.series"
                :key="s.period"
                class="flex items-center gap-3"
              >
                <span class="flex-shrink-0 w-28 text-base text-n-slate-11">{{
                  s.label
                }}</span>
                <div class="flex-1 h-7 rounded-md bg-n-solid-1 overflow-hidden">
                  <div
                    class="h-full rounded-md bg-n-brand"
                    :style="{
                      width: `${Math.max(2, (s.spend / maxSeriesSpend) * 100)}%`,
                    }"
                  />
                </div>
                <span
                  class="flex-shrink-0 w-32 text-base font-semibold text-right text-n-slate-12"
                  >{{ formatCurrency(s.spend) }}</span
                >
              </div>
            </div>
          </section>

          <!-- ====== Günlük reklam performansı (tarih · ciro · ROAS · not) ======
               Semra Hanım'ın istediği sütunlar: rakamların yanında o günün
               notu dursun ki "neden böyle olmuş" sonradan hatırlansın. -->
          <section
            v-if="(report.series || []).length"
            class="p-5 mb-6 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <h2 class="mb-1 text-xl font-bold text-n-slate-12">
              Günlük Reklam Performansı
            </h2>
            <p class="mb-4 text-sm text-n-slate-11">
              ROAS = reklamın getirdiği ciro ÷ harcama. Ciro ve satış Meta'nın
              ölçümüdür. Not alanına yazıp kutunun dışına tıklayınca kaydedilir.
            </p>
            <div class="overflow-x-auto">
              <table class="w-full text-base">
                <thead class="text-n-slate-10">
                  <tr>
                    <th class="py-2 pr-3 font-semibold text-left">Tarih</th>
                    <th class="py-2 pr-3 font-semibold text-right">Harcama</th>
                    <th class="py-2 pr-3 font-semibold text-right">Ciro</th>
                    <th class="py-2 pr-3 font-semibold text-right">ROAS</th>
                    <th class="py-2 pr-3 font-semibold text-right">Satış</th>
                    <th class="py-2 pr-3 font-semibold text-right">
                      Satış başı
                    </th>
                    <th class="py-2 font-semibold text-left">Reklam notu</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in report.series"
                    :key="row.period"
                    class="border-t border-n-weak"
                  >
                    <td class="py-2 pr-3 whitespace-nowrap text-n-slate-12">
                      {{ row.label }}
                    </td>
                    <td
                      class="py-2 pr-3 text-right tabular-nums text-n-slate-12"
                    >
                      {{ formatCurrency(row.spend) }}
                    </td>
                    <td
                      class="py-2 pr-3 text-right tabular-nums text-n-slate-12"
                    >
                      {{ formatCurrency(row.revenue) }}
                    </td>
                    <td
                      class="py-2 pr-3 text-right tabular-nums"
                      :class="roasClass(row.roas)"
                    >
                      {{ row.roas === null ? '—' : `${row.roas}x` }}
                    </td>
                    <td
                      class="py-2 pr-3 text-right tabular-nums text-n-slate-12"
                    >
                      {{ row.metaPurchases }}
                    </td>
                    <td
                      class="py-2 pr-3 text-right tabular-nums text-n-slate-11"
                    >
                      {{
                        row.costPerPurchase
                          ? formatCurrency(row.costPerPurchase)
                          : '—'
                      }}
                    </td>
                    <td class="py-2">
                      <div class="flex items-center gap-2">
                        <input
                          :value="noteValue(row)"
                          type="text"
                          placeholder="o gün ne yaptık / ne oldu"
                          class="w-full px-3 py-1.5 text-sm rounded-lg bg-n-solid-1 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
                          @input="noteDrafts[row.period] = $event.target.value"
                          @change="saveNote(row)"
                          @keyup.enter="saveNote(row)"
                        />
                        <span
                          v-if="noteSaving[row.period]"
                          class="text-xs text-n-slate-10"
                          >kaydediliyor…</span
                        >
                        <span
                          v-else-if="noteSaved[row.period]"
                          class="text-xs text-n-teal-11"
                          >kaydedildi ✓</span
                        >
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- ============ Dönüşüm hunisi ============ -->
          <section
            class="p-5 mb-6 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <h2 class="mb-4 text-xl font-bold text-n-slate-12">
              Dönüşüm Hunisi
            </h2>
            <div class="flex flex-col gap-2">
              <div
                v-for="row in funnelRows"
                :key="row.label"
                class="flex items-center gap-3"
              >
                <span class="flex-shrink-0 w-40 text-base text-n-slate-12">{{
                  row.label
                }}</span>
                <div class="flex-1 h-7 rounded-md bg-n-solid-1 overflow-hidden">
                  <div
                    class="h-full rounded-md bg-n-teal-9"
                    :style="{ width: `${Math.max(2, row.pct)}%` }"
                  />
                </div>
                <span
                  class="flex-shrink-0 w-28 text-base font-semibold text-right text-n-slate-12"
                  >{{ num(row.value) }}</span
                >
              </div>
            </div>
          </section>

          <!-- ============ En çok harcayan kampanyalar ============ -->
          <section
            v-if="(report.topCampaigns || []).length"
            class="p-5 mb-6 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <h2 class="mb-3 text-xl font-bold text-n-slate-12">
              En Çok Harcayan Kampanyalar
            </h2>
            <div class="flex flex-col gap-2">
              <div
                v-for="c in report.topCampaigns"
                :key="c.id"
                class="flex items-center justify-between gap-3 p-3 rounded-xl bg-n-solid-1"
              >
                <span
                  class="text-base font-medium truncate text-n-slate-12"
                  :title="c.name"
                  >{{ c.name }}</span
                >
                <span
                  class="flex-shrink-0 text-base font-bold text-n-slate-12"
                  >{{ formatCurrency(c.spend) }}</span
                >
              </div>
            </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>
